// @flow
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import CheckBoxGroup from '../CheckBoxGroup';
import Input from 'Common/Input';

// Helpers
import classNames from 'classnames';

/**
 * <AdvancedFiltersResults /> component.
 * @return {class} returns BasicFiltersMultiSelect instance
 */
class AdvancedFiltersResults extends PureComponent<*, *> {
  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.state = {
      selected: ''
    };
  }

  /**
   * componentDidMount
   * @return {void}
   */
  componentDidMount() {
    document.addEventListener('click', this.closeSwoosh);
  }

  /**
   * componentWillUnmount
   * @return {void}
   */
  componentWillUnmount() {
    document.removeEventListener('click', this.closeSwoosh);
  }

  /**
   * Implements propTypes().
   *
   * @return {Object} - propType object.
   * @prop {object} styles - styles.
   * @prop {object} fields - fields.
   * @prop {array} models - models.
   * @prop {number} priceFrom - priceFrom.
   * @prop {number} priceTo - priceTo.
   * @prop {function} checkPostcode - checkPostcode.
   * @prop {boolean} isPostcodeValid - isPostcodeValid.
   */
  static propTypes = {
    styles: PropTypes.object.isRequired,
    fields: PropTypes.object,
    resetField: PropTypes.func,
    additonalOptions: PropTypes.object,
    resultsLength: PropTypes.number
  };

  /**
   * setAsSelected
   *
   * @param {string} name - name.
   * @return {void}
   */
  setAsSelected = (name: String) => {
    this.setState({
      selected: name
    });
  };

  /**
   * closeSwoosh
   *
   * @param {object} event - event.
   * @return {void}
   */
  closeSwoosh = event => {
    const selectedCheckBoxGroup = document.querySelectorAll(
      '[data-selectedcheckboxgroup=true]'
    )[0];

    if (
      selectedCheckBoxGroup &&
      !selectedCheckBoxGroup.contains(event.target)
    ) {
      setTimeout(() => this.setAsSelected(''), 300);
    }
  };

  /**
   * Renders <BasicFilters /> component.
   *
   * @return {JSX} - renders a logo.
   */
  render() {
    const { styles, fields, resetField, resultsLength } = this.props;
    const {
      fuelTypes,
      transmissions,
      category,
      colours,
      doors,
      seats,
      mileages,
      annualTax,
      fuelConsumption,
      co2Emissions,
      acceleration,
      insuranceGroup,
      yearFrom,
      yearTo,
      engineSizeFrom,
      engineSizeTo
    } = this.props.additonalOptions;

    return (
      <div>
        <div className={classNames(styles.formRowItem)}>
          <CheckBoxGroup
            styles={styles}
            name={'category'}
            label={'Body type'}
            selectedValue={fields.category}
            options={category.filter(
              _ =>
                _.key !== 'other' && _.key !== 'pick up' && _.key !== 'unknown'
            )}
            isSelected={this.state.selected === 'category'}
            onSelect={() => this.setAsSelected('category')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            disableFiltering={true}
            isCarTypes={true}
          />

          <CheckBoxGroup
            styles={styles}
            name={'fuelType'}
            label={'Fuel type'}
            selectedValue={fields.fuelType}
            options={fuelTypes}
            isSelected={this.state.selected === 'fuelType'}
            onSelect={() => this.setAsSelected('fuelType')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            disableFiltering={true}
          />

          <CheckBoxGroup
            styles={styles}
            name={'gearBox'}
            label={'Gearbox'}
            selectedValue={fields.gearBox}
            options={transmissions}
            isSelected={this.state.selected === 'gearBox'}
            onSelect={() => this.setAsSelected('gearBox')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            disableFiltering={true}
          />

          <Input
            styles={styles}
            name={'keyword'}
            label={'Keyword'}
            selectedValue={fields.keyword}
            isLabelHidden={true}
            placeholderText={'Enter here'}
            isValid={fields.keyword && resultsLength > 0}
          />

          <CheckBoxGroup
            styles={styles}
            name={'annualTax'}
            label={'Annual tax'}
            selectedValue={fields.annualTax}
            options={annualTax}
            isSelected={this.state.selected === 'annualTax'}
            onSelect={() => this.setAsSelected('annualTax')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            isSwoosh={true}
          />

          <CheckBoxGroup
            styles={styles}
            name={'yearFrom'}
            label={'Year from'}
            selectedValue={fields.yearFrom}
            options={yearFrom}
            isSelected={this.state.selected === 'yearFrom'}
            onSelect={() => this.setAsSelected('yearFrom')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            isSwoosh={true}
          />

          <CheckBoxGroup
            styles={styles}
            name={'yearTo'}
            label={'Year to'}
            selectedValue={fields.yearTo}
            options={yearTo}
            isSelected={this.state.selected === 'yearTo'}
            onSelect={() => this.setAsSelected('yearTo')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            isSwoosh={true}
          />

          <CheckBoxGroup
            styles={styles}
            name={'mileage'}
            label={'Mileage'}
            selectedValue={fields.mileage}
            options={mileages}
            isSelected={this.state.selected === 'mileage'}
            onSelect={() => this.setAsSelected('mileage')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            disableFiltering={true}
          />

          <CheckBoxGroup
            styles={styles}
            name={'fuelConsumption'}
            label={'Fuel consumption'}
            selectedValue={fields.fuelConsumption}
            options={fuelConsumption}
            isSelected={this.state.selected === 'fuelConsumption'}
            onSelect={() => this.setAsSelected('fuelConsumption')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            disableFiltering={true}
          />

          <CheckBoxGroup
            styles={styles}
            name={'co2Emissions'}
            label={'CO2 Emissions'}
            selectedValue={fields.co2Emissions}
            options={co2Emissions}
            isSelected={this.state.selected === 'co2Emissions'}
            onSelect={() => this.setAsSelected('co2Emissions')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            isSwoosh={true}
          />

          <CheckBoxGroup
            styles={styles}
            name={'insuranceGroup'}
            label={'Insurance group'}
            selectedValue={fields.insuranceGroup}
            options={insuranceGroup}
            isSelected={this.state.selected === 'insuranceGroup'}
            onSelect={() => this.setAsSelected('insuranceGroup')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            disableFiltering={true}
          />

          <CheckBoxGroup
            styles={styles}
            name={'colour'}
            label={'Colour'}
            selectedValue={fields.colour}
            options={colours}
            isSelected={this.state.selected === 'colour'}
            onSelect={() => this.setAsSelected('colour')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            disableFiltering={true}
          />

          <CheckBoxGroup
            styles={styles}
            name={'doors'}
            label={'Doors'}
            selectedValue={fields.doors}
            options={doors}
            isSelected={this.state.selected === 'doors'}
            onSelect={() => this.setAsSelected('doors')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            disableFiltering={true}
          />

          <CheckBoxGroup
            styles={styles}
            name={'seats'}
            label={'Seats'}
            selectedValue={fields.seats}
            options={seats}
            isSelected={this.state.selected === 'seats'}
            onSelect={() => this.setAsSelected('seats')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            disableFiltering={true}
          />

          <CheckBoxGroup
            styles={styles}
            name={'acceleration'}
            label={'Acceleration'}
            selectedValue={fields.acceleration}
            options={acceleration}
            isSelected={this.state.selected === 'acceleration'}
            onSelect={() => this.setAsSelected('acceleration')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            disableFiltering={true}
          />

          <CheckBoxGroup
            styles={styles}
            name={'engineSizeFrom'}
            label={'Engine size from'}
            selectedValue={fields.engineSizeFrom}
            options={engineSizeFrom}
            isSelected={this.state.selected === 'engineSizeFrom'}
            onSelect={() => this.setAsSelected('engineSizeFrom')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            disableFiltering={true}
          />

          <CheckBoxGroup
            styles={styles}
            name={'engineSizeTo'}
            label={'Engine size to'}
            selectedValue={fields.engineSizeTo}
            options={engineSizeTo}
            isSelected={this.state.selected === 'engineSizeTo'}
            onSelect={() => this.setAsSelected('engineSizeTo')}
            setAsSelected={this.setAsSelected}
            resetField={resetField}
            disableFiltering={true}
          />
        </div>
      </div>
    );
  }
}

export default AdvancedFiltersResults;



// WEBPACK FOOTER //
// ./src/Results/AdvancedFiltersResults/index.js